import { FormControl, FormHelperText } from "@mui/material";
import moment, { Moment } from "moment";

import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { CustomFormikSubProps } from "@/common/helpers/formik";
import { ContractSpotInfoDto, ContractSpotInfoInputDto, SpotType } from "@/core/api/generated/v1";

import AppTextArea from "../../Form/Input/AppTextArea";
import SpotAutocompleteOrCreate from "../Spot/SpotAutocompleteOrCreate";
import { SpotDefaultValues } from "../Spot/SpotCreateUpdate";
import VisualInspectionAutocompleteOrCreate from "../VisualInspection/VisualInspectionAutocompleteOrCreate";

interface Props {
  contractId: string | null | undefined;
  spotInfo: ContractSpotInfoDto | null | undefined;
  formikProps: CustomFormikSubProps<ContractSpotInfoInputDto | null | undefined>;
  spotType: SpotType | undefined | null;
  spotDefaultValues?: SpotDefaultValues | null;
  onDateChange?: (newValue: Moment | null) => void;
}

export default function ContractSpotInfoInput({
  contractId,
  spotInfo,
  formikProps,
  spotType,
  spotDefaultValues,
  onDateChange,
}: Props) {
  const { values, errors, setFieldValue } = formikProps;

  return (
    <>
      <FormControl margin='dense' fullWidth error={Boolean(errors?.date)}>
        <AppDateTimePicker
          disabled={spotInfo?.isCompleted}
          label='Date & Time'
          value={(values?.date && moment(values.date)) || null}
          format={DATETIME_FORMATS.DISPLAY_DATETIME}
          onChange={(newValue) => {
            setFieldValue("date", newValue?.format() || null);
            onDateChange && onDateChange(newValue || null);
          }}
        />
        <FormHelperText error>{errors?.date}</FormHelperText>
      </FormControl>

      <FormControl margin='dense' fullWidth error={Boolean(errors?.spotId)}>
        <SpotAutocompleteOrCreate
          autocompleteProps={{
            entityId: values?.spotId,
            isPreload: false,
            disabled: spotInfo?.isCompleted,
            searchFilters: {
              type: spotType || undefined,
            },
            onChange: (newValue) => {
              setFieldValue("spotId", newValue?.id);
            },
          }}
          createUpdateProps={{
            defaultValues: spotDefaultValues,
          }}
          createFormPlacement='modal'
          onCreate={(newValue) => {
            setFieldValue("spotId", newValue?.id);
          }}
        />
        <FormHelperText error>{errors?.spotId}</FormHelperText>
      </FormControl>

      <FormControl margin='dense' fullWidth error={Boolean(errors?.visualInspectionId)}>
        <VisualInspectionAutocompleteOrCreate
          autocompleteProps={{
            entityId: values?.visualInspectionId,
            isPreload: false,
            disabled: spotInfo?.isCompleted,
            searchFilters: {
              contractId: contractId,
            },
            onChange: (newValue) => {
              setFieldValue("visualInspectionId", newValue?.id);
            },
          }}
          createFormPlacement='modal'
          onCreate={(newValue) => {
            setFieldValue("visualInspectionId", newValue?.id);
          }}
        />
        <FormHelperText error>{errors?.visualInspectionId}</FormHelperText>
      </FormControl>

      <FormControl margin='dense' fullWidth>
        <AppTextArea
          mode='notes'
          value={values?.notes || ""}
          error={Boolean(errors?.notes)}
          helperText={errors?.notes}
          onChange={(e) => {
            setFieldValue("notes", e.target.value);
          }}
        />
      </FormControl>
    </>
  );
}
